import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import * as cc from 'smg/cc';
import { Carriers } from 'smg/customer-api/carriers';
import * as models from 'smg/customer-api/models';
import { Orders } from 'smg/customer-api/orders';
import { Shipments } from 'smg/customer-api/shipments';
import { Loader } from 'smg/multiloader';
import { DollarFormat } from 'smg/util';
import { TicketTypeLabel } from './design-ticket-status';
import { Relref } from './hugo';
import i18n from './i18n';
import { ProofFormat, ProofItem, ProofStack } from './proof-stack';

// The customer-facing name of a mold: the quote-request project it was
// drawn for, else the product line it was last ordered as. proofs.Name is
// a filename (PROOF-2.jpg) and never shown.
// A mold card's identity, mirroring the API's design-identity rule (proofs.go
// proofsMoldSiblingWhere): the front mold, a frontless proof's back mold, and
// only then the proof itself.
export function MoldIdentity(proofInit: models.IHyejooProof): string {
    const proof = new models.HyejooProof(proofInit);
    return proof.frontMoldID || proof.backMoldID || `proof:${proof.id}`;
}

export function MoldLabel(proofInit: models.IHyejooProof): string {
    const proof = new models.HyejooProof(proofInit);
    if (proof.projectName) {
        return proof.projectName;
    }
    const lineItem = proof.latestOrderLineItem;
    if (lineItem) {
        const plural = lineItem.lineItemModel?.pluralName || lineItem.lineItemModel?.name || '';
        const type = lineItem.lineItemType?.name || '';
        const size = lineItem.lineItemTypeSize?.name || lineItem.size || '';
        return [`${lineItem.quantity} × ${plural}`, [type, size].filter(Boolean).join(', ')].filter(Boolean).join(' — ');
    }
    const newest = proof.orderProofs[0]?.order;
    if (newest?.invoiceNumber) {
        return i18n('moldFromOrder').replace('%s', String(newest.invoiceNumber));
    }
    return '';
}

export interface TimelineEvent {
    id: string;
    created: string;
    title: string;
    meta?: string;
    href: string;
    icon: string;
    tone: 'primary' | 'secondary' | 'success' | 'danger' | 'warning';
    // The artwork this event is about. Empty for the events that stand for no
    // picture at all — a payment, a recorded PO, a mold warning.
    art?: ProofItem[];
}

export function RelativeTime(created: DateTime): string {
    if (!created.isValid) {
        return '';
    }
    const now = DateTime.now();
    if (now.diff(created, 'minutes').minutes < 1) {
        return i18n('timelineJustNow');
    }
    // Intl.RelativeTimeFormat, in the page's locale: minutes/hours today,
    // "yesterday" / "3 days ago" inside a week, then the plain date.
    const days = Math.floor(now.startOf('day').diff(created.startOf('day'), 'days').days);
    if (days === 0) {
        return created.toRelative() ?? '';
    }
    if (days < 7) {
        return created.toRelativeCalendar() ?? '';
    }
    return created.toLocaleString(DateTime.DATE_MED);
}

export function TimelineItem(e: TimelineEvent, lastSeenISO?: string): HTMLElement {
    // A div, not one big <a>: the picture opens the artwork, and a control
    // cannot live inside a link. The title carries the row's destination.
    const $row = document.createElement('div');
    $row.className = 'ov-feed-row d-flex align-items-center gap-3 py-2 px-1 text-body';
    $row.dataset.created = e.created;
    $row.dataset.search = i18n('accountSearchKindActivity');
    $row.dataset.searchLabel = e.title;

    const created = DateTime.fromISO(e.created);
    if (lastSeenISO) {
        const lastSeen = DateTime.fromISO(lastSeenISO);
        if (lastSeen.isValid && created.isValid && created > lastSeen) {
            $row.classList.add('is-new');
        }
    }

    // The picture takes the icon's place and wears the event kind as a badge;
    // an event with no picture behind it keeps the plain icon.
    const $art = ProofStack(e.art ?? [], {
        size: 'sm',
        max: 2,
        badge: e.icon,
        badgeTone: e.tone,
        enlarge: true,
    });
    if ($art) {
        $row.append($art);
    } else {
        const $icon = document.createElement('span');
        $icon.className = `ov-feed-icon icon-mark icon-mark-xs icon-mark-${e.tone}`;
        const $ms = document.createElement('span');
        $ms.className = 'material-symbols-rounded';
        $ms.setAttribute('aria-hidden', 'true');
        $ms.textContent = e.icon;
        $icon.append($ms);
        $row.append($icon);
    }

    const $body = document.createElement('span');
    $body.className = 'ov-feed-body flex-grow-1';
    const $title = document.createElement('a');
    $title.className = 'ov-feed-title d-block text-decoration-none text-body';
    $title.href = e.href;
    const $strong = document.createElement('b');
    $strong.textContent = e.title;
    $title.append($strong);
    $body.append($title);
    if (e.meta) {
        const $meta = document.createElement('small');
        $meta.className = 'text-muted d-block';
        $meta.textContent = e.meta;
        $body.append($meta);
    }
    $row.append($body);

    const $time = document.createElement('span');
    $time.className = 'ov-feed-time text-muted flex-shrink-0';
    $time.textContent = RelativeTime(created);
    $row.append($time);

    return $row;
}

// The picture behind a proof. getURLs().main is the on-screen rendition — a
// vector or PDF proof comes back rasterized, so every proof draws.
function proofArt(proof: models.Proof | undefined, alt: string): ProofItem[] {
    const src = proof?.getURLs().main ?? '';
    return src ? [{ src, alt }] : [];
}

// An order's artwork: every proof on it. Most orders carry one; a real minority
// carry several, and the stack is what keeps that from growing the row.
export function OrderArt(orderInit: models.IHyejooOrder | undefined): ProofItem[] {
    if (!orderInit) {
        return [];
    }
    const order = new models.HyejooOrder(orderInit);
    const alt = i18n('accountOrderNumber').replace('%s', String(order.invoiceNumber));
    const art: ProofItem[] = [];
    for (const orderProof of order.orderProofs) {
        const src = orderProof.proof?.getURLs().main ?? '';
        if (src) {
            art.push({ src, alt: orderProof.name || alt });
        }
    }
    return art;
}

// A design ticket's picture: the proof the artist drew, and before one exists
// the customer's own uploaded image from the request.
export function TicketArt(taskInit: models.IHyejooQuoteRequestProjectTask, alt: string): ProofItem[] {
    const task = new models.HyejooQuoteRequestProjectTask(taskInit);
    const art = proofArt(task.quoteProof?.proof, alt);
    if (art.length > 0) {
        return art;
    }
    const upload = task.quoteRequest?.uploads?.find(u => u.mime.startsWith('image/') && u.publicURL !== '');
    return upload ? [{ src: upload.publicURL, alt, mime: upload.mime }] : [];
}

function iso(dt: DateTime | undefined): string {
    if (!dt?.isValid) {
        return '';
    }
    return dt.toISO() ?? '';
}

function orderHref(id: string): string {
    return `${Relref('/account/order')}?id=${id}`;
}

function ticketHref(key: string): string {
    return `${Relref('/design-ticket')}?qrpt=${key}`;
}

function quoteHref(id: string): string {
    return `${Relref('/account/quote')}?id=${id}`;
}

function firstLine(text: string): string {
    return text.replace(/<[^>]+>/g, '').trim().split(/\n/)[0] ?? '';
}

function paymentDetail(row: models.OrderPayment): string | undefined {
    if (row.purchaseOrderNumber) {
        return row.purchaseOrderNumber;
    }
    const option = row.paymentOption;
    if (option?.creditCardLast4) {
        const brand = cc.Issuer(option.creditCardFirst6) || i18n('timelineCard');
        return i18n('timelineCardEnding').replace('{brand}', brand).replace('{last4}', String(option.creditCardLast4));
    }
    return undefined;
}

function ticketKind(task: models.QuoteRequestProjectTask): string {
    return TicketTypeLabel(task);
}

function terminalCreated(task: models.QuoteRequestProjectTask, statusID: number): string | undefined {
    const changelog = task.changelog;
    for (let i = changelog.length - 1; i >= 0; i--) {
        const entry = changelog[i];
        if (
            entry.column === 'QuoteRequestProjectTaskStatusID' &&
            Number(entry.newValue) === statusID &&
            entry.time.isValid
        ) {
            return iso(entry.time) || undefined;
        }
    }
    return undefined;
}

export interface TimelineLoadersOpts {
    lastSeenISO?: string;
    proofs?: () => Promise<models.HyejooProof[]>;
}

// Each loader pages its SOURCE rows by their created time and derives events
// dated by the event's own timestamp (shippedAt, deliveredAt, quoteSents…), so
// a very old row whose event happened recently can surface later than strict
// event order. Accepted tradeoff of the client-side MultiLoader pattern (no
// backend event feed exists); the per-customer search index (#9762) is the
// eventual home for a true event stream.
export function TimelineLoaders(opts: TimelineLoadersOpts): Loader<TimelineEvent>[] {
    const element = (e: TimelineEvent) => TimelineItem(e, opts.lastSeenISO);
    let carriersPromise: ReturnType<typeof Carriers> | undefined;

    // A shipment carries its order's id but not its artwork — the /shipments
    // `order` projection is id, invoiceNumber and userID. The ids only exist
    // once that response lands, by which point the loader's Batch is already
    // sent, so this is a plain request. The cache keeps an order that ships in
    // several boxes from being fetched once per box.
    const orderArtCache = new Map<string, ProofItem[]>();
    async function orderArtByID(ids: string[]): Promise<Map<string, ProofItem[]>> {
        const need = [...new Set(ids)].filter(id => id && !orderArtCache.has(id));
        if (need.length === 0) {
            return orderArtCache;
        }
        try {
            const resp = await Orders({
                q: `id:${need.join(',')}`,
                data: 'id,invoiceNumber,orderProofs',
                max: need.length,
            });
            for (const order of resp.results) {
                orderArtCache.set(order.id, OrderArt(order));
            }
        } catch {
            // The rows draw without artwork rather than not at all.
        }
        for (const id of need) {
            if (!orderArtCache.has(id)) {
                orderArtCache.set(id, []);
            }
        }
        return orderArtCache;
    }

    const loaders: Loader<TimelineEvent>[] = [
        new Loader<TimelineEvent>({
            name: 'orders',
            load: (b: Batch, page: number, pageSize: number) => Orders({
                sort: 'created desc',
                max: pageSize,
                p: page,
                data: 'invoiceNumber,key,created,confirmedTime,total,customerShipDate,orderProofs',
            }).then(resp => {
                const rows: TimelineEvent[] = [];
                for (const row of resp.results) {
                    const art = OrderArt(row);
                    if (row.created.isValid) {
                        rows.push({
                            id: `placed-${row.id}`,
                            created: iso(row.created),
                            title: i18n('timelineOrderPlaced').replace('%s', String(row.invoiceNumber)),
                            meta: DollarFormat(row.total),
                            href: orderHref(row.id),
                            icon: 'shopping_bag',
                            tone: 'primary',
                            art,
                        });
                    }
                    if (row.confirmedTime.isValid) {
                        const ships = row.customerShipDate.isValid ?
                            i18n('timelineShipsBy').replace('%s', row.customerShipDate.toLocaleString(DateTime.DATE_MED)) :
                            undefined;
                        rows.push({
                            id: `production-${row.id}`,
                            created: iso(row.confirmedTime),
                            title: i18n('timelineOrderInProduction').replace('%s', String(row.invoiceNumber)),
                            meta: ships,
                            href: orderHref(row.id),
                            icon: 'factory',
                            tone: 'primary',
                            art,
                        });
                    }
                }
                return { rows, done: !resp.nextPage };
            }),
            element,
        }),

        new Loader<TimelineEvent>({
            name: 'shipments',
            load: (b: Batch, page: number, pageSize: number) => {
                if (!carriersPromise) {
                    carriersPromise = Carriers({ data: 'name,link' }, b).catch(() => new Map<NonNullable<models.Carrier['id']>, models.Carrier>());
                }
                return Promise.all([
                    Shipments({
                        sort: 'created desc',
                        max: pageSize,
                        p: page,
                        data: 'id,created,carrierID,trackingNumber,shippedAt,deliveredAt,exceptionAt,order',
                    }),
                    carriersPromise,
                ]).then(async ([resp, carriers]) => {
                    const art = await orderArtByID(resp.results.map(row => row.order?.id ?? ''));
                    const rows: TimelineEvent[] = [];
                    for (const row of resp.results) {
                        const invoice = String(row.order?.invoiceNumber ?? '');
                        const href = row.order?.id ? orderHref(row.order.id) : Relref('/account/orders');
                        const orderArtwork = art.get(row.order?.id ?? '') ?? [];
                        const carrierName = row.carrier?.name || (row.carrierID ? carriers.get(row.carrierID)?.name : '') || '';
                        const trackMeta = [carrierName, row.trackingNumber].filter(Boolean).join(' · ');
                        // shippedAt when the carrier scanned it; a quarter of
                        // real shipments never get the scan, so the row's
                        // created time is the fallback shipped signal.
                        if (row.shippedAt.isValid || row.created.isValid) {
                            const when = row.shippedAt.isValid ? row.shippedAt : row.created;
                            rows.push({
                                id: `shipped-${row.id}`,
                                created: iso(when),
                                title: i18n('timelineOrderShipped').replace('%s', invoice),
                                meta: trackMeta || undefined,
                                href,
                                icon: 'local_shipping',
                                tone: 'success',
                                art: orderArtwork,
                            });
                        }
                        if (row.deliveredAt.isValid) {
                            rows.push({
                                id: `delivered-${row.id}`,
                                created: iso(row.deliveredAt),
                                title: i18n('timelineOrderDelivered').replace('%s', invoice),
                                href,
                                icon: 'check_circle',
                                tone: 'success',
                                art: orderArtwork,
                            });
                        }
                        if (row.exceptionAt.isValid) {
                            rows.push({
                                id: `exception-${row.id}`,
                                created: iso(row.exceptionAt),
                                title: i18n('timelineDeliveryException').replace('%s', invoice),
                                meta: trackMeta || undefined,
                                href,
                                icon: 'error',
                                tone: 'danger',
                                art: orderArtwork,
                            });
                        }
                    }
                    return { rows, done: !resp.nextPage };
                });
            },
            element,
        }),

        new Loader<TimelineEvent>({
            name: 'payments',
            load: (b: Batch, page: number, pageSize: number) => b.get('/order-payments', {
                params: {
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                    data: 'total,created,paymentOption,orderPayments,purchaseOrderNumber',
                },
            }).then(r => {
                const resp = models.OrderPaymentsHyejooResponse.fromJSON(r.data);
                const rows: TimelineEvent[] = [];
                for (const row of resp.orderPayments) {
                    if (!row.id) {
                        continue;
                    }
                    // A PO row can be a zero-total placeholder — the PO was
                    // recorded, no money moved; "Payment received" is reserved
                    // for positive payments.
                    const isPO = row.paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO ||
                        Boolean(row.purchaseOrderNumber);
                    let title: string;
                    if (isPO) {
                        title = row.total.gt(0) ? i18n('timelinePurchaseOrderPayment').replace('%s', DollarFormat(row.total)) : i18n('timelinePurchaseOrderRecorded');
                    } else if (row.total.gt(0)) {
                        title = i18n('timelinePaymentReceived').replace('%s', DollarFormat(row.total));
                    } else {
                        continue;
                    }
                    const order = row.orderPayments[0]?.order;
                    rows.push({
                        id: `payment-${row.id}`,
                        created: iso(row.created),
                        title,
                        meta: paymentDetail(row),
                        href: order?.id ? orderHref(order.id) : Relref('/account/payment-history'),
                        icon: 'payments',
                        tone: 'success',
                    });
                }
                return { rows, done: !resp.nextPage };
            }),
            element,
        }),

        new Loader<TimelineEvent>({
            name: 'quotes',
            load: (b: Batch, page: number, pageSize: number) => b.get('/v2/quotes', {
                params: {
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                    data: 'serial,created,key,quoteProofs,quoteSents,cachedTotal',
                },
            }).then(r => {
                const resp = models.QuotesHyejooResponse.fromJSON(r.data);
                const rows: TimelineEvent[] = [];
                for (const quote of resp.quotes) {
                    const href = quoteHref(quote.id);
                    const quoteArt: ProofItem[] = [];
                    for (const entry of quote.quoteProofs) {
                        const src = entry.proof?.getURLs().main ?? '';
                        if (src) {
                            quoteArt.push({ src, alt: i18n('timelineQuoteEmailed').replace('%s', String(quote.serial)) });
                        }
                    }
                    for (const entry of quote.quoteProofs) {
                        if (!entry.created?.isValid) {
                            continue;
                        }
                        const event: TimelineEvent = {
                            id: `proof-${entry.id ?? ''}`,
                            created: iso(entry.created),
                            title: entry.number === undefined || entry.number === null || String(entry.number) === '' ?
                                i18n('timelineProofAdded') :
                                i18n('timelineProofNumberAdded').replace('%s', String(entry.number)),
                            href,
                            icon: 'image',
                            tone: 'secondary',
                            art: proofArt(entry.proof, i18n('timelineProofNumberAdded').replace('%s', String(entry.number ?? ''))),
                        };
                        rows.push(event);
                    }
                    for (const entry of quote.quoteSents) {
                        if (!entry.created.isValid) {
                            continue;
                        }
                        rows.push({
                            id: `sent-${entry.id}`,
                            created: iso(entry.created),
                            title: i18n('timelineQuoteEmailed').replace('%s', String(quote.serial)),
                            meta: DollarFormat(quote.cachedTotal),
                            href,
                            icon: 'mail',
                            tone: 'secondary',
                            art: quoteArt,
                        });
                    }
                }
                return { rows, done: !resp.nextPage };
            }),
            element,
        }),

        new Loader<TimelineEvent>({
            name: 'designTickets',
            load: (b: Batch, page: number, pageSize: number) => b.get('/quote-request-project-tasks', {
                params: {
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                    data: 'id,key,created,quoteRequestProjectTaskStatusID,quoteRequestProjectTaskTypeID,quoteRequest,quoteProof,lastUserEvent,changelog',
                },
            }).then(r => {
                const resp = models.QuoteRequestProjectTasksHyejooResponse.fromJSON(r.data);
                const rows: TimelineEvent[] = [];
                for (const row of resp.quoteRequestProjectTasks) {
                    const href = ticketHref(row.key);
                    const name = row.quoteRequest?.quoteRequestProject?.name || i18n('timelineDesignTicket');
                    const kind = ticketKind(row);
                    const art = TicketArt(row, name);
                    if (row.created.isValid) {
                        rows.push({
                            id: `ticket-open-${row.id}`,
                            created: iso(row.created),
                            title: i18n('timelineTicketOpened').replace('%s', kind),
                            meta: name,
                            href,
                            icon: 'design_services',
                            tone: 'primary',
                            art,
                        });
                    }

                    const statusID = row.quoteRequestProjectTaskStatusID;
                    // Artwork milestones leave the ticket open (the quote is
                    // still coming — TicketClosed); only SENT completes it.
                    const isArtDone =
                        statusID === models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE ||
                        statusID === models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_VECTOR_DONE;
                    const isSent = statusID === models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT;
                    const isCanceled = statusID === models.QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED;
                    if (isArtDone || isSent) {
                        const createdAt = terminalCreated(row, statusID);
                        if (createdAt) {
                            rows.push({
                                id: `ticket-done-${row.id}`,
                                created: createdAt,
                                title: isSent ? i18n('timelineTicketCompleted') : i18n('timelineArtworkFinished'),
                                meta: name,
                                href,
                                icon: isSent ? 'check_circle' : 'palette',
                                tone: 'success',
                                art,
                            });
                        }
                    } else if (isCanceled) {
                        const createdAt = terminalCreated(row, statusID);
                        if (createdAt) {
                            rows.push({
                                id: `ticket-canceled-${row.id}`,
                                created: createdAt,
                                title: i18n('timelineTicketCanceled'),
                                meta: name,
                                href,
                                icon: 'error',
                                tone: 'danger',
                                art,
                            });
                        }
                    }

                    const last = row.lastUserEvent;
                    // The hydrated event carries fromAdmin, never userID — an
                    // admin-authored event is the designer speaking.
                    if (last?.id && last.fromAdmin && last.created.isValid) {
                        const message = firstLine(last.textContent || last.content);
                        rows.push({
                            id: `ticket-reply-${row.id}-${last.id}`,
                            created: iso(last.created),
                            title: i18n('timelineDesignerReplied'),
                            meta: message ? `${name} — ${message}` : name,
                            href,
                            icon: 'forum',
                            tone: 'primary',
                            art,
                        });
                    }
                }
                return { rows, done: !resp.nextPage };
            }),
            element,
        }),

        new Loader<TimelineEvent>({
            name: 'uploads',
            load: (b: Batch, page: number, pageSize: number) => b.get('/images', {
                params: {
                    q: 'type:upload',
                    data: 'id,name,created,key,mime',
                    sort: 'created desc',
                    max: pageSize,
                    p: page,
                },
            }).then(r => {
                const resp = models.ImagesResponse.fromJSON(r.data);
                const rows: TimelineEvent[] = [];
                for (const result of resp.results) {
                    const upload = result.upload;
                    if (!upload?.id || !upload.created.isValid) {
                        continue;
                    }
                    // convertible is decided at response time against the
                    // current formats — a file we cannot rasterize shows its
                    // format instead, the way the Files page does.
                    const url = upload.url();
                    const art: ProofItem[] = url ? [{
                        src: result.convertible ? url : '',
                        alt: upload.name,
                        format: result.convertible ? undefined : ProofFormat(upload.name, upload.mime),
                        mime: upload.mime,
                        originalURL: url,
                    }] : [];
                    rows.push({
                        id: `upload-${upload.id}`,
                        created: iso(upload.created),
                        title: i18n('timelineYouUploaded').replace('%s', upload.name),
                        href: Relref('/account/files'),
                        icon: 'upload_file',
                        tone: 'secondary',
                        art,
                    });
                }
                return { rows, done: !resp.nextPage };
            }),
            element,
        }),
    ];

    if (opts.proofs) {
        const proofsOf = opts.proofs;
        loaders.push(new Loader<TimelineEvent>({
            name: 'molds',
            load: () => proofsOf().then(proofs => {
                const today = DateTime.now().startOf('day');
                const seen = new Set<string>();
                const rows: TimelineEvent[] = [];
                for (const proof of proofs) {
                    const moldID = MoldIdentity(proof);
                    if (!moldID || seen.has(moldID) || !proof.moldExpires.isValid) {
                        continue;
                    }
                    seen.add(moldID);
                    if (proof.moldExpires < today) {
                        continue;
                    }
                    const days = proof.moldExpires.startOf('day').diff(today, 'days').days;
                    if (days > 30) {
                        continue;
                    }
                    const warnAt = proof.moldExpires.minus({ days: 30 });
                    const newestInvoice = proof.orderProofs[0]?.order?.invoiceNumber;
                    const fromOrder = !proof.projectName && !proof.latestOrderLineItem && newestInvoice;
                    let title: string;
                    if (fromOrder) {
                        title = i18n('timelineMoldFromOrderExpires').replace('%s', String(newestInvoice));
                    } else {
                        title = i18n('timelineMoldForExpires').replace('%s', MoldLabel(proof) || i18n('moldThisDesign'));
                    }
                    rows.push({
                        id: `mold-warn-${moldID}`,
                        created: iso(warnAt),
                        title,
                        meta: i18n('timelineMoldReorderNote'),
                        href: '#ov-molds',
                        icon: 'schedule',
                        tone: 'warning',
                    });
                }
                return { rows, done: true };
            }).catch(() => ({ rows: [] as TimelineEvent[], done: true })),
            element,
        }));
    }

    return loaders;
}
